import React, { useContext, useEffect, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
    useLoaderData, useLocation, useNavigate, useSearchParams,
} from 'react-router-dom';

import DescriptorT from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import { filterDescriptorsByParams } from '../utils/filterDescriptorsByParams';

import LeftArrow from '@/assets/icon-arrow-left.svg?react';
import RightArrow from '@/assets/icon-arrow-right.svg?react';
import { RapidStorSettings } from '@/core/utils/rapidstor';
import { fadeIn, fadeOut } from '@/core/utils/ui';

import store from '@/store';
import {
    selectContactCard,
    setContactCard,
    setReservationData,
    setUnitData,
    setUpsell,
} from '@/store/bookingSlice';
import { prepareBookingNavigation } from '@/journey-based-listing/utils/prepareBookingNavigation';

import Alert from '@/core/components/elements/alert';
import Button from '@/core/components/elements/button';
import { FormType } from '@/core/components/elements/contact-card';
import Header from '@/core/components/elements/header';
import JourneyProgress from '@/core/components/journey-progress';
import Descriptor from '@/journey-based-listing/components/descriptor';
import SizeSlider from '@/journey-based-listing/components/SizeSlider';
import ReactSimplyCarousel, { NavDirection } from '@/journey-based-listing/components/UnitListing/Carousel';

import {
    firstAvailableUnitType,
    isDescriptorAvailable,
} from '@/features/rapidstor/helpers';
import { unitListingLoadedEvent } from '@/features/rapidstor/PostMessageEvents';
import { AnalyticsContext } from '@/features/rapidstor/providers/AnalyticsProvider';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';

import descriptorClasses from '@/journey-based-listing/components/descriptor/styles.module.scss';
import classes from '@/scss/screens/unit-listing.module.scss';
import useRapidStorTracking from '@/core/hooks/useRapidStorTracking';
import Spinner from '@/core/components/elements/spinner';

export default function UnitListing() {
    /**
   * General page data
   */
    const location = useLoaderData() as Location;
    const locationData = useLocation();
    const navigate = useNavigate();
    const [cookies] = useCookies(['contactData']);
    const [personaCookie, setPersonaCookie] = useCookies(['persona']);
    const [searchParams, setSearchParams] = useSearchParams();
    const { firebaseLogEvent } = useContext(AnalyticsContext);
    const {
        persona,
        unitList,
        unitListSuccess,
        unitListLoading,
        setPersona,
        descriptorList,
        unavailableDescriptorIds,
        rapidstorMainColor,
        dealSelectorVariant,
        reservationData,
    } = useContext(JourneyContext);
    const contactCard = useSelector(selectContactCard);
    const { t } = useTranslation();
    const startPosition = `${window.rapidstor_settings.rapidstor_options.startPosition}`
        || searchParams.get('startPosition')
        || '0';
    const personaId = searchParams.get('personaId')
        || window.rapidstor_settings.rapidstor_options.persona
        || persona
        || personaCookie.persona
        || undefined;

    // Source descriptors: Prefer unitList.descriptors if present, else descriptorList
    const sourceDescriptors = React.useMemo<DescriptorT[]>(() => {
        if (unitList?.descriptors && unitList.descriptors.length > 0) {
            return unitList.descriptors;
        }
        return descriptorList ?? [];
    }, [unitList?.descriptors, descriptorList]);

    /**
     * Apply URL param filtering
     */
    const filteredDescriptorList = React.useMemo<DescriptorT[]>(
        () => filterDescriptorsByParams(sourceDescriptors, searchParams),
        [sourceDescriptors, searchParams],
    );

    useEffect(() => {
        if (personaId && personaId !== persona) {
            setPersona(personaId);
            searchParams.delete('personaId');
            setSearchParams(searchParams);
            setPersonaCookie('persona', personaId, {
                path: '/',
                maxAge: 60 * 60 * 24 * 7,
            });
        }
    }, [
        personaId,
        persona,
        searchParams,
        setPersona,
        setPersonaCookie,
        setSearchParams,
    ]);

    /**
   * UI and Unit state
   */
    const [sizes, setSizes] = useState<number[]>([]);
    const [defaultSelected, setDefaultSelected] = useState<number>(0);
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [descriptor, setDescriptor] = useState<DescriptorT>();
    const [carouselDirection, setCarouselDirection] = useState<NavDirection>('forward');

    const { trackEvent } = useRapidStorTracking();

    const [timesMoved, setTimesMoved] = useState<number>(0);

    const showLoading = unitListLoading;
    const showNoResults = !unitListLoading && filteredDescriptorList.length === 0;
    const showResults = !unitListLoading && filteredDescriptorList.length > 0 && descriptor && unitListSuccess;
    const showCarouselEndCTA = window.rapidstor_settings.rapidstor_options.enabledCarouselEndCTA;

    /**
   * Highest index the carousel can show. The end CTA adds one extra slide.
   */
    const maxIndex = Math.max(
        showCarouselEndCTA ? filteredDescriptorList.length : filteredDescriptorList.length - 1,
        0,
    );

    /**
   * The list gets shorter when the persona changes. Keep the index in range so
   * a stale index does not read an item that does not exist.
   */
    const safeIndex = Math.min(Math.max(selectedIndex, 0), maxIndex);

    const carouselOnMove = (newIndex: number) => {
        const nextIndex = Math.min(Math.max(newIndex, 0), maxIndex);
        setCarouselDirection(selectedIndex > nextIndex ? 'backward' : 'forward');
        setSelectedIndex(nextIndex);
        setTimesMoved(timesMoved + 1);
    };

    /**
   * Uses the descriptorList to get the area of each unit and sorts them ascending
   * @param units Descriptor[]
   * @returns number[]
   */
    const getUnitSizes = (units: DescriptorT[]) => {
        const areaArr: number[] = [];
        units.forEach((unit) => {
            areaArr.push(unit.area);
        });
        return areaArr.sort((a, b) => parseFloat(a.toString()) - parseFloat(b.toString()));
    };

    /**
   * Fires the unitListingLoadedEvent and the rapidstorTrackEvent after the unitList response is loaded
   */
    useEffect(() => {
        if (unitListSuccess && !unitListLoading) {
            unitListingLoadedEvent();
            trackEvent('unit-listing', {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitListSuccess, unitListLoading]);

    useEffect(() => {
        fadeIn();

        if (!filteredDescriptorList.length) {
            setDescriptor(undefined);
            setSelectedIndex(0);
            setDefaultSelected(0);
            setSizes([]);
            return;
        }

        const newIndex = (() => {
            // Priority 1: If the URL has startPosition and it's valid
            const pos = parseInt(startPosition);
            if (!Number.isNaN(pos) && pos < filteredDescriptorList.length) return pos;

            // Priority 2: If location state points to an unavailable descriptor
            if (locationData.state?.unavailableDescriptor) {
                const idx = filteredDescriptorList.findIndex(
                    (d) => d._id === locationData.state.unavailableDescriptor,
                );
                if (idx > -1) return idx;
            }

            // Default: first filtered descriptor
            return 0;
        })();

        setDefaultSelected(newIndex);
        setSelectedIndex(newIndex);
        setDescriptor(filteredDescriptorList[newIndex]);

        const unitSizes = getUnitSizes(filteredDescriptorList);
        setSizes(showCarouselEndCTA ? [...unitSizes, -1] : unitSizes);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredDescriptorList, startPosition, locationData.state]);

    /**
     * The biggest unit the customer saw before reaching the end slide. Sent with
     * the larger-unit enquiry so the operator knows what they have ruled out.
     * Picked by area rather than list position, which is not sorted.
     */
    const largestUnitTypeShown = React.useMemo<UnitType | undefined>(() => {
        if (!filteredDescriptorList.length) {
            return undefined;
        }
        const largest = filteredDescriptorList.reduce(
            (max, item) => (item.area > max.area ? item : max),
        );
        return firstAvailableUnitType(largest.unitTypes || [], location);
    }, [filteredDescriptorList, location]);

    const toggleContactCard = (type: FormType, unitType?: UnitType) => {
        store.dispatch(
            setContactCard({
                animate: 'start',
                type,
                unitType: unitType ?? null,
                isDeal: !!unitType?.deal,
                deal: unitType?.deal?._id as string,
            }),
        );
    };

    const goToBookingPage = async () => {
        const descriptorFromSelectedIndex = filteredDescriptorList[safeIndex];
        if (descriptorFromSelectedIndex) {
            const unitType = firstAvailableUnitType(
                descriptorFromSelectedIndex.unitTypes || [],
                location,
            );
            store.dispatch(
                setUnitData({
                    location,
                    unitType,
                    descriptor: descriptorFromSelectedIndex,
                    descriptors: filteredDescriptorList,
                }),
            );

            const showContactCardButton = (!isDescriptorAvailable(descriptorFromSelectedIndex)
                || unavailableDescriptorIds.includes(descriptorFromSelectedIndex._id))
                && !RapidStorSettings(location).alwaysAllowPricingPage()
                && RapidStorSettings(location).showSaveQuoteButton();

            if (showContactCardButton) {
                store.dispatch(
                    setReservationData({
                        descriptor: descriptorFromSelectedIndex,
                        unitType,
                    }),
                );
                toggleContactCard('contactMe', unitType);
                return;
            }

            firebaseLogEvent('unit_listing_carousel_scroll_count', {
                amount: `${timesMoved}`,
            });
            firebaseLogEvent('unit_listing_selected', {});

            const hidePrices = window.rapidstor_settings?.app_config?.journey?.hidePrices
                ?? location.rapidstor?.hidePrices;

            if (hidePrices && !cookies.contactData) {
                store.dispatch(
                    setReservationData({
                        descriptor: descriptorFromSelectedIndex,
                        unitType,
                    }),
                );
                toggleContactCard('showPrices', unitType);
            } else if (dealSelectorVariant) {
                const result = prepareBookingNavigation(
                    descriptorFromSelectedIndex,
                    filteredDescriptorList,
                    location,
                    reservationData ?? undefined,
                    dealSelectorVariant,
                );
                if (!result) {
                    navigate('/');
                    return;
                }
                const { reservationData: newReservationData, upsellDescriptor } = result;
                store.dispatch(setReservationData(newReservationData));
                if (upsellDescriptor) {
                    store.dispatch(setUpsell(upsellDescriptor));
                }
                fadeOut();
                setTimeout(() => {
                    navigate('/customise-booking');
                }, 400);
            } else {
                fadeOut();
                setTimeout(() => {
                    navigate('/book-unit');
                }, 400);
            }
        }
    };

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (contactCard.animate !== 'initial') {
                return;
            }
            if (event.key === 'ArrowLeft') {
                if (safeIndex === 0) {
                    return;
                }
                setCarouselDirection('backward');
                setTimeout(() => {
                    setSelectedIndex(safeIndex - 1);
                    setTimesMoved(timesMoved + 1);
                }, 50);
            } else if (event.key === 'ArrowRight') {
                if (safeIndex >= maxIndex) {
                    return;
                }
                setCarouselDirection('forward');
                setTimeout(() => {
                    setSelectedIndex(safeIndex + 1);
                    setTimesMoved(timesMoved + 1);
                }, 50);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [safeIndex, maxIndex, timesMoved, contactCard.animate]);

    return (
        <>
            <div
                style={{
                    padding: '0 16px',
                }}
            >
                <JourneyProgress mobile />
                <div>
                    <div className={classes.results_header}>
                        <div className="d-flex">
                            <Header
                                level={1}
                                className={classes.title}
                                data-testid="unitlisting-header"
                            >
                                {t('unit_listing:header_1')}
                                <span className="underline">{t('unit_listing:header_2')}</span>
                                {t('unit_listing:header_3')}
                            </Header>
                        </div>
                        <JourneyProgress desktop />

                        {showLoading && (
                            <Spinner />
                        )}

                        {showNoResults && (
                            <div style={{ padding: '32px 0', textAlign: 'center' }}>
                                <Alert
                                    type="info"
                                    text="Sorry, no results match these filters."
                                    fitContent
                                />
                            </div>
                        )}

                        {showResults && (
                            <>
                                {locationData.state?.unitUnavailableAtMoveIn && (
                                    <Alert
                                        type="warning"
                                        text={t('unit_listing:unit_unavailable_at_move_in')}
                                        fitContent
                                    />
                                )}

                                <div className={classes.mobile}>
                                    {showCarouselEndCTA
                                        && safeIndex === filteredDescriptorList.length ? (
                                            <div
                                                className={classes.descriptor_wrapper}
                                                style={{
                                                opacity: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                            >
                                                <div className={descriptorClasses.descriptor}>
                                                    <div
                                                        style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '25px',
                                                        minHeight: '250px',
                                                        textAlign: 'center',
                                                    }}
                                                    >
                                                        <span
                                                            style={{
                                                            fontSize: '36px',
                                                            fontWeight: '600',
                                                            lineHeight: '38px',
                                                        }}
                                                        >
                                                            {t('cta:bigger_unit')}
                                                        </span>
                                                        <Button
                                                            type="primary"
                                                            size="small"
                                                            onclick={() => toggleContactCard('largerUnit', largestUnitTypeShown)}
                                                        >
                                                            {location.rapidstor.noAvailableUnitsText}
                                                        </Button>
                                                    </div>
                                                    {window.rapidstor_settings.app_config.journey.showArrowsOnDescriptorMobile ? (
                                                        <div className={descriptorClasses.mobile_buttons}>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                if (safeIndex > 0) {
                                                                    setSelectedIndex(safeIndex - 1);
                                                                    setTimesMoved(timesMoved + 1);
                                                                    setCarouselDirection('backward');
                                                                }
                                                            }}
                                                                disabled={false}
                                                            >
                                                                <LeftArrow />
                                                            </button>
                                                            <button type="button" onClick={undefined} disabled>
                                                                <RightArrow />
                                                            </button>
                                                        </div>
                                                ) : null}
                                                    <div className={descriptorClasses.footer} />
                                                </div>
                                            </div>
                                    ) : (
                                        <Descriptor
                                            key={filteredDescriptorList[safeIndex]._id}
                                            descriptor={filteredDescriptorList[safeIndex]}
                                            onSelect={goToBookingPage}
                                            carousel={false}
                                            selectedIndex={safeIndex}
                                            total={filteredDescriptorList.length}
                                            setSelectedIndex={carouselOnMove}
                                        />
                                    )}
                                </div>

                                <div className={`${classes.desktop}`}>
                                    <ReactSimplyCarousel
                                        activeSlideIndex={safeIndex}
                                        onRequestChange={(index) => {
                                            setSelectedIndex(Math.min(Math.max(index, 0), maxIndex));
                                            setTimesMoved(timesMoved + 1);
                                        }}
                                        sliderDirection={carouselDirection}
                                        containerProps={{
                                            style: {
                                                width: '100%',
                                                justifyContent: 'space-between',
                                                userSelect: 'none',
                                            },
                                        }}
                                        preventScrollOnSwipe
                                        swipeTreshold={60}
                                        hideNavIfAllVisible={false}
                                        forwardBtnProps={{
                                            style: {
                                                alignSelf: 'center',
                                            },
                                            disabled: safeIndex >= maxIndex,
                                        }}
                                        activeSlideProps={{
                                            style: {
                                                opacity: 1,
                                            },
                                        }}
                                        backwardBtnProps={{
                                            style: {
                                                alignSelf: 'center',
                                            },
                                            disabled: safeIndex === 0,
                                        }}
                                        itemsToShow={1}
                                        speed={300}
                                    >
                                        {filteredDescriptorList.map(
                                            (d: DescriptorT, index: number) => (
                                                <div
                                                    key={d._id}
                                                    className={classes.descriptor_wrapper}
                                                >
                                                    <Descriptor
                                                        descriptor={d}
                                                        carousel={false}
                                                        active={index === safeIndex}
                                                        onSelect={goToBookingPage}
                                                    />
                                                </div>
                                            ),
                                        )}
                                        {showCarouselEndCTA ? (
                                            <div
                                                className={classes.descriptor_wrapper}
                                                style={{
                                                    width: '86vw',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <div
                                                    className={classes.descriptor}
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '24px',
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            fontSize: '24px',
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        {t('cta:bigger_unit')}
                                                    </span>
                                                    <Button
                                                        type="primary"
                                                        size="small"
                                                        onclick={() => toggleContactCard('largerUnit', largestUnitTypeShown)}
                                                    >
                                                        {location.rapidstor.noAvailableUnitsText}
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : null}
                                    </ReactSimplyCarousel>
                                </div>
                            </>
                        )}

                        {!showLoading && !showNoResults && !showResults && (
                            <>
                                <div className={`${classes.mobile}`}>
                                    <ContentLoader
                                        foregroundColor={rapidstorMainColor ?? location.skin.mainColour ?? '#eee'}
                                        backgroundColor="#f5f5f5"
                                        width="100%"
                                        height="100vw"
                                    >
                                        <rect
                                            x="0"
                                            y="4"
                                            rx="3"
                                            ry="3"
                                            width="100%"
                                            height="100%"
                                        />
                                    </ContentLoader>
                                </div>

                                <div className={`${classes.desktop}`}>
                                    <ContentLoader
                                        foregroundColor={rapidstorMainColor ?? location.skin.mainColour ?? '#eee'}
                                        backgroundColor="#f5f5f5"
                                        width="100%"
                                        height="399px"
                                    >
                                        <rect
                                            x="0"
                                            y="4"
                                            rx="3"
                                            ry="3"
                                            width="100%"
                                            height="100%"
                                        />
                                    </ContentLoader>
                                </div>

                                <ContentLoader
                                    foregroundColor={rapidstorMainColor ?? location.skin.mainColour ?? '#eee'}
                                    backgroundColor="#f5f5f5"
                                    width="100%"
                                    height="60px"
                                >
                                    <rect x="0" y="24" rx="3" ry="3" width="100%" height="100%" />
                                </ContentLoader>
                            </>
                        )}
                    </div>
                </div>
            </div>
            {descriptor && location && filteredDescriptorList && defaultSelected !== undefined
                && filteredDescriptorList.length > 0 && (
                <div
                    style={{
                        paddingTop: '8px',
                        paddingLeft: '24px',
                        paddingRight: '24px',
                    }}
                >
                    <SizeSlider
                        sizes={sizes}
                        activeStep={safeIndex}
                        onSetActiveStep={carouselOnMove}
                    />
                </div>
            )}
        </>
    );
}
